/**
 * @description: RouteRecordRaw 参数说明
 * @param: path {string} 跳转路径
 * @param: redirect {string} 重定向跳转路径
 * @param: component {string} 组件
 * @param: meta {object} 可以自定义一些内容
 *         visible {boolean} 一级菜单只有一个二级菜单时是否始终显示。（因为有的一级菜单只有一个二级菜单）
 *         hidden {Boolean} 默认false， 为true表示此路由不作为菜单展示
 *         hideAside {Boolean} 默认false， 为true表示隐藏左侧导航
 *         icon {string} 菜单对应的图标icon, 推荐使用svg形式
 *         title {string} 菜单对应的标题，也作为浏览器标签标题展示
 *         authCodes {array} 用户权限code
 *         operations {array} 当前页面组件用户有权限的操作的 code 列表
 *         isBlank {boolean} 默认false， 是否需要在浏览器新标签页中打开
 *         activeMenu {string} 激活菜单的index, index取的是路由中path的值, 解决有不在菜单中展示的页面时菜单激活指定菜单
 *         navbarActiveMenu {string} 激活顶部导航菜单的index, index取的是路由中path的值
 *
 *         setBreadcrumbs {function} 重置面包屑内容
 *         iframeSrc {string} 页面内嵌iframe的地址，注册路由时请使用 @/layouts/iframe/index.vue
 *         isPageReused {boolean} 默认false 如果两个路由使用同一个组件，一定要设置isPageReused: true
 *         keepAlive {boolean} 默认false，页面是否被缓，true为缓存页面
 *         onlyOneChild {boolean} 默认false，直接展示一级菜单的情况使用该属性
 *         isThreeModel {boolean} 默认false 如果设置为true，表示是3D模型页面
 *         selectProjectDisabled {boolean} 默认false 如果设置为true，左上角项目树选择将被禁用
 *         hideSelectProject {boolean} 默认false 如果设置为true，左上角项目树选择将不显示
 *         projectCheckStrictly {boolean} 默认false 如果设置为true，左上角项目树选择将只能选择最后子节点
 *         selectProjectMultiple {boolean} 左上角项目选择是否支持多选
 *         targetUrl 直接跳转到外部链接地址
 * @return void {*}
 */
import {
  createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized,
} from 'vue-router';
import config from '@/config';

import { useAppStore } from '@/store/app';
import { useUserStore } from '@/store/user';
import { useOrgStore } from '@/store/org';
import { usePermissionStore } from '@/store/permission';
import { useTenantStore } from '@/store/tenant';

import Layout from '@/layouts/index.vue';
import Redirect from '@/layouts/redirect/index.vue';
import Page401 from '@/views/401.vue';
import Page404 from '@/views/404.vue';
import { homeRoutes } from './modules/home'; // 首页
import { astmRoutes } from './modules/astm'; // 资产
import { tcyRoutes } from './modules/tcy'; // 通行
import { systemRoutes } from './modules/system'; // 系统页
import { accountRoutes } from './modules/account'; // 账号页
import { safetyRoutes } from './modules/safety'; // 安全页
import { spaceRoutes } from './modules/space'; // 空间
import { ossRoutes } from './modules/operation'; // 运维-报警
import { energyConservationRoutes } from './modules/energy-conservation'; // 能源 3d表页
import { ibmsRoutes } from './modules/ibms'; // 运行页
import { configurationRoutes } from './modules/configuration'; // 组态图

const viteProjectRoot = import.meta.env.VITE_APP_ROOT;
let appStore: TypeObject;
let userStore: TypeObject;
let orgStore: TypeObject;
let permissionStore: TypeObject;
let tenantStore: TypeObject;

export const constantRoutes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login/index.vue'),
    meta: {
      hidden: true,
    },
  }
];

export const catchAllRoutes: Array<RouteRecordRaw> = [
  {
    path: '/redirect',
    component: Layout,
    name: 'redirect',
    meta: {
      hidden: true,
    },
    children: [
      {
        path: '/redirect/:catchAll(.*)',
        component: Redirect,
      }
    ],
  },
  {
    path: '/error',
    name: 'error',
    component: Layout,
    redirect: '/error/404',
    meta: {
      hidden: true,
    },
    children: [
      {
        path: '/error/404',
        name: '404',
        component: Page404,
        meta: {
          hidden: true,
        },
      },
      {
        path: '/error/401',
        name: '401',
        component: Page401,
        meta: {
          hidden: true,
          hideAside: true,
          hideSelectProject: true,
        },
      }
    ],
  }
];

export const asyncRoutes: Array<RouteRecordRaw> = [
  ...tcyRoutes,
  ...astmRoutes,
  ...homeRoutes,
  ...systemRoutes,
  ...accountRoutes,
  ...ibmsRoutes,
  ...safetyRoutes,
  ...spaceRoutes,
  ...ossRoutes,
  ...energyConservationRoutes,
  ...configurationRoutes
];

const router = createRouter({
  history: createWebHistory(viteProjectRoot),
  routes: [...constantRoutes, ...catchAllRoutes],
  scrollBehavior: () => ({
    left: 0,
    top: 0,
  }),
});

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: Func) => {
  // 登录页无需获取用户信息、租户等接口，直接放行
  if (to.path === '/login') {
    next();
    return;
  }

  if (!appStore) {
    appStore = useAppStore();
  }
  if (!userStore) {
    userStore = useUserStore();
  }
  if (!orgStore) {
    orgStore = useOrgStore();
  }
  if (!permissionStore) {
    permissionStore = usePermissionStore();
  }
  if (!tenantStore) {
    tenantStore = useTenantStore();
  }

  // 需要根据条件动态设置面包屑的时候可以在meta中设置setBreadcrumbs函数
  if (typeof to.meta.setBreadcrumbs === 'function') {
    to.meta.breadcrumbs = to.meta.setBreadcrumbs(to);
  }
  // 使用同一个页面组件的不同路由
  const isPageReused = from.meta.isPageReused && to.meta.isPageReused;
  // 动态生成子应用容器路由
  config.microAppsActiveRules.forEach((rule) => {
    router.addRoute({
      path: `/${rule}/:chapters*`,
      component: Layout,
      name: `${rule}-routes-container`,
      meta: {
        hidden: true,
      },
    });
  });
  const hasMenus = userStore.menus && userStore.menus.length > 0;
  console.log('hasMenus', hasMenus, new Date().toLocaleTimeString());
  if (hasMenus) {
    if (!orgStore?.orgList || orgStore?.orgList?.length === 0) { // 第一次进入系统，当内存中用户项目信息为空时，主动调用接口获取项目信息。
      await orgStore.getOrgTreeData();
    }
    console.log('to', to, from);
    if (to.path === '/insight/carous/index') {
      if (from.path === '/') {
        next();
      } else {
        window.open(`${window.location.origin}${to.path}`, '_blank');
        return;
      }
    }
    if (to.meta.targetUrl) {
      window.open(to.meta.targetUrl as string, '_blank');
      return;
    }
    if (isPageReused) {
      next(`/redirect${to.fullPath}`);
    } else {
      next();
    }
  } else {
    // 先获取租户列表，确保租户ID在请求头中
    try {
      await tenantStore.getTenantList();
    } catch (error) {
      console.error('获取租户列表失败:', error);
    }

    // 域名匹配检查：当前域名与租户配置的域名不一致时，跳转到租户配置的域名
    const currentHostname = window.location.hostname;
    const currentExpandInfo = tenantStore.currentTenant?.expandInfo;
    let tenantDomain: string | undefined;
    if (currentExpandInfo) {
      try {
        const info = typeof currentExpandInfo === 'string' ? JSON.parse(currentExpandInfo) : currentExpandInfo;
        tenantDomain = info?.tenantDomain;
      } catch {
        // 解析失败忽略
      }
    }

    const [{ authKeys }, { menus }] = await Promise.all([userStore.getUserInfo(), userStore.getMenuList()]);
    const { accessedRoutes, permissionRoutes } = await permissionStore.generateRoutes(authKeys, menus);
    console.log('根据用户权限动态生成的路由:', accessedRoutes);

    // 用户有系统权限，主动调用一下获取全局字典的接口
    if (accessedRoutes.length) {
      appStore.getDicList();
    }

    //  设置用户输入 / 跳转至有权限的第一个路由（按 menuList 顺序）
    const findFirstAccessiblePath = (menusList: any[]): string | null => {
      // 从租户 store 获取权限编码前缀
      const fmPermission = tenantStore.fmPermission;

      for (const menu of menusList) {
        // 检查当前菜单是否有权限（通过 authCodes 判断）
        const menuAuthCodes = menu.authCodes || [];
        const matchedCodes = menuAuthCodes.filter((code: string) => authKeys.includes(`${fmPermission}${code}`));

        if (matchedCodes.length > 0) {
          // 如果有 children，递归查找第一个有权限的子菜单
          if (menu.children && menu.children.length > 0) {
            const childPath = findFirstAccessiblePath(menu.children);
            if (childPath) {
              return childPath;
            }
          } else {
            // 没有 children，直接返回当前菜单的 path
            return menu.path;
          }
        }
      }
      return null;
    };

    const firstAccessiblePath = findFirstAccessiblePath(menus);

    if (firstAccessiblePath) {
      accessedRoutes.unshift({
        path: '/',
        redirect: firstAccessiblePath,
        meta: {
          hidden: true,
        },
      });
    }
    // 动态添加路由
    accessedRoutes.forEach((item: any) => {
      router.addRoute(item);
    });

    //  配置404
    router.addRoute({
      path: '/:catchAll(.*)',
      redirect: '/error/404',
      meta: {
        hidden: true,
      },
    });

    // 用户没有菜单权限 进入401 用户无权限页面
    if (!authKeys.length) {
      if (config.excludePath.includes(to.path)) {
        next();
        return;
      }
      next('/error/401');
      return;
    }

    next({ ...to, replace: true });
  }
});

router.afterEach((to: RouteLocationNormalized) => {
  setTimeout(() => {
    (document.getElementById('mainLoader') as any).style.display = 'none';
    const baseTitle = import.meta.env.VITE_APP_NAME_ZH;
    window.document.title = to.meta.title ? `${to.meta.title} | ${baseTitle}` : baseTitle;
  }, 0);
});

export default router;
